<template>
     <v-form
        ref="fieldLocationForm"
    >
    <app-card-code
    :title="title"
    :subtitle="sampleRules"
    :code="codeSnippet"
    flat
    outlined
    class="mt-4"
    >
    <template slot="title"  class="text-subtitle-1 py-2 grey lighten-4">
      {{ title }}
    </template>
    <v-card-text class="pt-4">
      <v-row v-if="locations.length === 0" justify="center">
        <v-btn color="success" text @click="addField">
          <v-icon left>{{ icons.mdiPlus }}</v-icon> Add Initial Location
        </v-btn>
      </v-row>

      <v-row v-for="(input, index) in locations" :key="index" align="center" dense class="mb-2">
        <v-col cols="12" md="6">
          <v-autocomplete
            v-if="!input.field_location"
            v-model="input.field_choice"
            :items="availableFieldLocation"
            item-text="name"
            label="Select Location"
            :hint="`Physical Facility`"
            persistent-hint
            outlined
            dense
            hide-details="auto"
            return-object
            @change="fieldSelection(index, $event)"
            >
            <template #selection="{ item }">
                <span class="font-weight-medium">{{ item.name }}</span>
            </template>

            <template #item="{ item, attrs, on }">
                <v-list-item v-on="on" v-bind="attrs" class="px-4">
                <v-list-item-avatar color="primary lighten-5" size="32">
                    <v-icon color="primary" small>{{ icons.mdiMapMarkerOutline }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                    <v-list-item-title class="text-subtitle-2">{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="caption">{{ item.supplier }}</v-list-item-subtitle>
                </v-list-item-content>
                </v-list-item>
            </template>
            </v-autocomplete>
            <v-text-field
                v-else
                v-model="input.field_name"
                :label="`Location ${index + 1}`"
                :hint="`Supplier: ${input.field_supplier}`"
                persistent-hint
                :prepend-inner-icon="icons.mdiMapMarkerCheck"
                outlined
                dense
                disabled
                hide-details="auto"
                class="bg-light"
            ></v-text-field>
        </v-col>

        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="input.sample"
            label="Sample Size"
            type="number"
            :hint="`Sample size`"
            persistent-hint
            outlined dense 
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="2" class="d-flex">
          <v-btn icon color="success" small @click="addField" v-if="canAddMore">
            <v-icon>{{ icons.mdiPlus }}</v-icon>
          </v-btn>
          <v-btn icon color="error" small @click="removeField(index)">
            <v-icon>{{ icons.mdiMinus }}</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    </app-card-code>
    </v-form>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import useProject from '../../useProject'
import { mdiPlus, mdiMinus, mdiMapMarkerCheck, mdiMapMarkerOutline } from '@mdi/js'
import AppCardCode from '@/@core/components/app-card-code/AppCardCode.vue'

import {
  fieldLocationCodeSnippet
} from './code'

export default {
    components:{
        AppCardCode
    },
  props: {
    title: String,
    fieldLocations: Array,
    fieldCountry: Object,
    projectType: Number,
  },
  setup(props) {
    const locations = ref(props.fieldLocations)
    const { fieldLocationLoading, fieldLocationOptions, fetchFieldLocations } = useProject()
    
    fetchFieldLocations()

    const canAddMore = computed(() => {
      return locations.value.length < filteredFieldLocationCountry.value.length
    })

    const filteredFieldLocationCountry = computed(() => {
      return fieldLocationOptions.value.filter(item => 
        item.country === props.fieldCountry.country && 
        item.project_types.includes(props.projectType)
      )
    })

    const availableFieldLocation = computed(() => {
      const usedIds = locations.value.map(l => l.field_location)
      return filteredFieldLocationCountry.value.filter(loc => !usedIds.includes(loc.id))
    })

    const sumSample = computed(() => {
      const currentSum = locations.value.reduce((acc, curr) => acc + (curr.sample || 0), 0)
      return props.fieldCountry.sample - currentSum
    })

    const sampleRules = computed(() => {
      if (sumSample.value === 0) return {}
      return {
        text: sumSample.value < 0 ? `Over by ${Math.abs(sumSample.value)}` : `Missing ${sumSample.value}`,
        class: 'error--text font-weight-bold'
      }
    })

    const addField = () => {
      locations.value.push({ field_location: "", sample: 0, project_country: props.fieldCountry.id, field_name: "" })
    }

    const removeField = (index) => locations.value.splice(index, 1)

    const fieldSelection = (index, field) => {
      locations.value[index].field_location = field.id
      locations.value[index].field_name = field.name
      locations.value[index].field_supplier = field.supplier
    }
    
    const codeSnippet = computed(() => {
        return fieldLocationCodeSnippet(locations, fieldLocationOptions)
    })
    watch(
          () => props.fieldLocations, 
          () => {
              locations.value = props.fieldLocations
          }
      )

    return {
      locations, fieldLocationLoading, availableFieldLocation, canAddMore,
      sampleRules, sumSample, addField, removeField, fieldSelection,
      icons: { mdiPlus, mdiMinus, mdiMapMarkerCheck, mdiMapMarkerOutline },
      codeSnippet,
    }
  }
}
</script>

<style lang="scss" scoped>
.v-autocomplete__content .v-list-item {
  margin-bottom: 4px;
  
  .v-list-item__title {
    line-height: 1.4;
    margin-bottom: 2px;
  }
}
</style>